import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Link, 
  Divider, 
  Grid 
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';
import { 
  Business, 
  Person, 
  LocationOn, 
  Phone, 
  Article 
} from '@mui/icons-material';

const FOOTER_BG = '#E8D4BE';
const FOOTER_TEXT = '#3B2416';
const FOOTER_ICON = '#5C4033';
const FOOTER_LINK = '#6B3E26';

const Footer: React.FC = () => {
  const { t, currentLanguage } = useLanguageContext();
  const [logoFailed, setLogoFailed] = useState<boolean>(false);
  const [logoSrc] = useState<string>('/images/logo.png');
  
  return (
    <Box 
      component="footer"

      sx={{
        bgcolor: FOOTER_BG,
        color: FOOTER_TEXT,
        borderTop: 1,
        borderColor: 'rgba(92, 64, 51, 0.28)',
        mt: 'auto',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ color: FOOTER_TEXT, fontWeight: 700 }}>
              He Listens to Me
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1,
                  borderRadius: 1.25,
                  backgroundColor: '#E8F5E9',
                  border: '1px solid #C8E6C9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                }}
              >
                {logoFailed ? (
                  <Business sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt="Company Logo"
                    onError={() => setLogoFailed(true)}
                    sx={{
                      width: '72%',
                      height: '72%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.companyName')} 
                <Link 
                  href="https://www.advancedsocietyinitiative.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    color: FOOTER_LINK, 
                    fontWeight: 700,
                    textDecoration: 'underline',
                    ml: 0.5,
                    '&:hover': {
                      color: FOOTER_TEXT
                    }
                  }}
                >
                  {t('footer.companyNameValue')}
                </Link>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Person sx={{ mr: 1, fontSize: 'small', color: FOOTER_ICON }} />
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.representative')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Article sx={{ mr: 1, fontSize: 'small', color: FOOTER_ICON }} />
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.businessNumber')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Business sx={{ mr: 1, fontSize: 'small', color: FOOTER_ICON }} />
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.ecommerceNumber')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <LocationOn sx={{ mr: 1, fontSize: 'small', color: FOOTER_ICON, mt: 0.2 }} />
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.address')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ mr: 1, fontSize: 'small', color: FOOTER_ICON }} />
              <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
                {t('footer.phone')}
              </Typography>
            </Box>


          </Grid>

          {/* Legal Links */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ color: FOOTER_TEXT, fontWeight: 700 }}>
              {t('footer.policies')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link 
                component={RouterLink}
                to={currentLanguage === 'en' ? "/terms-en" : "/terms"}
                variant="body2"
                sx={{ 
                  color: FOOTER_LINK,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: FOOTER_TEXT
                  }
                }}
              >
                {t('footer.terms')}
              </Link>
              <Link 
                component={RouterLink}
                to={currentLanguage === 'en' ? "/privacy-en" : "/privacy"}
                variant="body2"
                sx={{ 
                  color: FOOTER_LINK,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: FOOTER_TEXT
                  }
                }}
              >
                {t('footer.privacy')}
              </Link>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(92, 64, 51, 0.28)' }} />

        {/* Copyright */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: FOOTER_TEXT }}>
            {t('footer.copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;